import fp from 'lodash/fp';
import React, { useState, useEffect, useCallback, useRef, useMemo, ComponentType, FC, ComponentProps } from 'react';
import { Registers, Supplies } from '@factorin/domain';

import { SignedIcon, Text, Button } from 'Modules/ui';
import { useAction } from 'Modules/core/redux';
import { useTranslation, TFunction } from 'Modules/core/i18n';
import { useEnvironment } from 'Modules/business/environment';
import { storageActions, useStorageItem } from 'Modules/business/data';
import { downloadsActions, useDownloads, DownloadsTypes } from 'Modules/business/downloads';

import { DownloadPopup, InfoSection, SectionHeader, FileSection, FileItem, FileItemProps } from '../../DownloadPopup';
import { DownloadButtonProps, RegisterSectionType } from './types';
import styles from './index.module.scss';


const SIGNING_PROTOCOL_TYPE_CODE = 'SigningProtocol';

const signatureFields = ['ClientORProvider', 'DebtorORBuyer', 'Factor'];
const registerHasAnySignature = fp.pipe(
    fp.pick(signatureFields.map(field => `${field}SignUid`)),
    fp.pickBy(fp.identity),
    fp.negate(fp.isEmpty),
);

const documentModel = (
    t: TFunction,
    register: Registers.RegisterDetails,
    download: (path: string, elem: DownloadsTypes.DownloadAction) => void,
    documentsDownloads: DownloadsTypes.Download,
): FileItemProps[] => register.Documents
    .filter(({ TypeCode }) => TypeCode !== SIGNING_PROTOCOL_TYPE_CODE)
    .map(({ Uid, Type, Number, Date }) => ({
        title: Type,
        description: Number,
        date: Date,
        onDownload: () => download('documents', { id: Uid }),
        progress: documentsDownloads[Uid],
    }));

const signatureModel = (
    t: TFunction,
    register: Registers.RegisterDetails,
    download: (path: string, elem: DownloadsTypes.DownloadAction) => void,
    signsDownloads: DownloadsTypes.Download,
    documentsDownloads: DownloadsTypes.Download,
): FileItemProps[] => fp.pipe(
    fp.filter(role => register[`${role}SignUid`]),
    fp.map(role => ({
        title: t(`titleSigns.${role}`),
        description: register[`${role}SignerName`],
        date: register[`${role}SignDate`],
        icon: SignedIcon,
        onDownload: () => download('signs', { id: register[`${role}SignUid`] }),
        progress: signsDownloads[register[`${role}SignUid`]],
    })),
    signs => [
        ...signs,
        ...registerHasAnySignature(register) ? [{
            title: t('titleSigns.protocolFile'),
            date: register.CreateDate,
            onDownload: () => download('documents', {
                method: 'downloadProtocol',
                id: register.Uid,
                uid: register.Uid,
            }),
            progress: documentsDownloads[register.Uid],
        }] : [],
    ],
)(signatureFields);

const RegisterSection: ComponentType<RegisterSectionType> = ({ t, files }) => files.length > 0
    ? (
        <FileSection>
            {files.map((file, idx) => (
                <FileItem key={idx} {...file} />
            ))}
        </FileSection>
    )
    : (
        <InfoSection>
            {t('emptyFiles')}
        </InfoSection>
    );

type RejectReasonProps = {
    reasonCode: Registers.RegisterDetails['RejectReasonCode'];
    t: TFunction;
}

const RejectReason: FC<RejectReasonProps> = ({ reasonCode, t }) => {
    const reasons = useEnvironment<Supplies.SupplyRejectReason>('suppliesRejectReasons');
    return (
        <Text<ComponentProps<'div'>> component='div'>
            {t('rejectReason', { reason: reasons[reasonCode].Name })}
        </Text>
    );
};

type DownloadAllProps = {
    uid: Registers.Register['Uid'];
    domain: DownloadButtonProps['domain'];
}

const DownloadAll: FC<DownloadAllProps> = ({ uid, domain }) => {
    const download = useAction(
        () => downloadsActions.download(domain, { id: uid }),
        [uid, domain],
    );
    const { t } = useTranslation('registersList');
    return (
        <Text
            component={Button}
            variant='auxiliary'
            textType='link'
            onClick={download}
        >
            {t('downloadAll')}
        </Text>
    );
};

export const DownloadButton: FC<DownloadButtonProps> = ({
    register: { Uid },
    domain = 'registers',
}) => {
    const register = useStorageItem<Registers.RegisterDetails>(domain, Uid);

    const shouldDownloadRegister = !register || !register.Documents;
    const [registerDownloading, setRegisterDownloading] = useState(false);
    const registerDownloadingTimer = useRef<ReturnType<typeof setTimeout>>();

    const signsDownloads = useDownloads('signs');
    const documentsDownloads = useDownloads('documents');
    const downloadInProgress = useMemo(
        () => !fp.isEmpty({
            ...signsDownloads,
            ...documentsDownloads,
        }),
        [signsDownloads, documentsDownloads],
    );

    const loadRegister = useAction(
        () => storageActions.loadItem(domain, Uid),
        [Uid, domain],
    );
    const onOpen = useCallback(
        () => {
            if (shouldDownloadRegister && !registerDownloading) {
                registerDownloadingTimer.current =
                    setTimeout(() => setRegisterDownloading(true), 100);
                loadRegister();
            }
        },
        [registerDownloading, loadRegister, shouldDownloadRegister],
    );

    const download = useAction(
        downloadsActions.download,
        [],
    );

    useEffect(
        () => {
            if (!shouldDownloadRegister) {
                clearTimeout(registerDownloadingTimer.current);
                setRegisterDownloading(false);
            }
        },
        [shouldDownloadRegister],
    );

    const { t } = useTranslation('registersList');

    const documentFiles = useMemo(
        () => shouldDownloadRegister
            ? []
            : documentModel(t, register, download, documentsDownloads),
        [shouldDownloadRegister, t, register, download, documentsDownloads],
    );

    const signFiles = useMemo(
        () => shouldDownloadRegister
            ? []
            : signatureModel(t, register, download, signsDownloads, documentsDownloads),
        [shouldDownloadRegister, t, register, download, signsDownloads, documentsDownloads],
    );

    return (
        <DownloadPopup
            onOpen={onOpen}
            suppressPopup={shouldDownloadRegister}
            isDownloading={registerDownloading || downloadInProgress}
        >
            {!shouldDownloadRegister && (
                <>
                    <SectionHeader className={styles.documentHeader}>
                        {t('documentSectionName')}
                        <DownloadAll domain={domain} uid={Uid} />
                    </SectionHeader>
                    <RegisterSection
                        t={t}
                        files={documentFiles}
                    />
                    <SectionHeader>
                        {t('signSectionName')}
                    </SectionHeader>
                    <RegisterSection
                        t={t}
                        files={signFiles}
                    />
                    {(register.RejectReasonCode || register.Comment) && (
                        <SectionHeader>
                            {t('comments')}
                        </SectionHeader>
                    )}
                    {register.RejectReasonCode && (
                        <RejectReason
                            reasonCode={register.RejectReasonCode}
                            t={t}
                        />
                    )}
                    {register.Comment && (
                        <Text<ComponentProps<'div'>> component='div'>
                            {register.Comment}
                        </Text>
                    )}
                </>
            )}
        </DownloadPopup>
    );
};
